/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Domain Bridge Store (Zustand)
   Reactive adapter connecting pure Graph Domain & Commands to React.
   Does NOT contain domain business logic.
   ═══════════════════════════════════════════════════════════════ */

import { create } from 'zustand';
import type {
  CanonicalGraph,
  DomainGraphState,
  NodeId,
} from '../domain/types';
import { createEmptyGraph, createDomainState } from '../domain/graph';
import type { CommandHistoryState, DomainCommand } from '../domain/commands';
import {
  createEmptyHistory,
  executeCommand,
  undoCommand,
  redoCommand,
} from '../domain/commands';
import { ExecutionScheduler, type ExecutionResult, type NodeOutputValues } from '../execution';
import { defaultNodeRegistry } from '../nodes';
import { WebGL2Backend } from '../rendering/webgl2/backend';
import { Canvas2DBackend } from '../rendering/canvas2d/backend';
import { detectRendererCapabilities } from '../rendering/capabilities';
import { AutosaveCoordinator } from '../persistence/autosave';

export interface DomainBridgeStore {
  // ── State ──
  domainState: DomainGraphState;
  history: CommandHistoryState;
  projectName: string;
  isExecuting: boolean;
  executionResult: ExecutionResult | null;
  nodeOutputs: Record<NodeId, NodeOutputValues>;
  saveStatus: 'saved' | 'saving' | 'dirty';

  // ── Scheduler Instance ──
  scheduler: ExecutionScheduler;

  // ── Command Dispatchers ──
  dispatch: (command: DomainCommand) => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  // ── Execution ──
  runExecution: (dirtyNodeIds?: Set<NodeId>) => Promise<void>;

  // ── Project Lifecycle ──
  setProjectName: (name: string) => void;
  loadCanonicalGraph: (canonical: CanonicalGraph, name?: string) => void;
  resetToNewProject: (name?: string) => void;
}

// Initialize GPU or fallback backend once
const capabilities = detectRendererCapabilities();
const backend = capabilities.webgl2 ? new WebGL2Backend() : new Canvas2DBackend();

const scheduler = new ExecutionScheduler(backend, capabilities, {
  getProcessor: (type: string) => defaultNodeRegistry.get(type)?.processor,
});

const autosave = new AutosaveCoordinator(2000);
let currentExecutionRunId = 0;

const LAYOUT_ONLY_COMMAND_TYPES = new Set(['node.move', 'nodes.move_multiple', 'node.collapse', 'frame.create', 'frame.delete']);

export function affectsCreativeExecution(command: DomainCommand): boolean {
  if (LAYOUT_ONLY_COMMAND_TYPES.has(command.descriptor.type)) return false;
  if (command.descriptor.type === 'batch' && 'commands' in command) {
    const commands = (command as DomainCommand & { commands: readonly DomainCommand[] }).commands;
    return commands.some(affectsCreativeExecution);
  }
  return true;
}

export const useDomainBridge = create<DomainBridgeStore>((set, get) => {
  // Sync autosave notifications
  autosave.onSaved(() => {
    set({ saveStatus: 'saved' });
  });

  // Listen for incremental node outputs from scheduler
  scheduler.onNodeExecuted((nodeId, outputs) => {
    set((s) => ({
      nodeOutputs: {
        ...s.nodeOutputs,
        [nodeId]: outputs,
      },
    }));
  });

  return {
    domainState: createEmptyGraph(),
    history: createEmptyHistory(),
    projectName: 'Untitled Studio Project',
    isExecuting: false,
    executionResult: null,
    nodeOutputs: {},
    saveStatus: 'saved',
    scheduler,

    dispatch: (command: DomainCommand) => {
      const { domainState, history } = get();
      const { nextState, nextHistory } = executeCommand(domainState, history, command);

      set({
        domainState: nextState,
        history: nextHistory,
        saveStatus: 'dirty',
      });

      // Trigger idle autosave
      autosave.markDirty(() => ({
        state: get().domainState,
        projectName: get().projectName,
      }));

      // Layout-only commands do not invalidate GPU processing.
      if (affectsCreativeExecution(command)) get().runExecution();
    },

    undo: () => {
      const { domainState, history } = get();
      const command = history.undoStack[history.undoStack.length - 1];
      const { nextState, nextHistory } = undoCommand(domainState, history);

      set({
        domainState: nextState,
        history: nextHistory,
        saveStatus: 'dirty',
      });

      autosave.markDirty(() => ({
        state: get().domainState,
        projectName: get().projectName,
      }));

      if (command && affectsCreativeExecution(command)) get().runExecution();
    },

    redo: () => {
      const { domainState, history } = get();
      const command = history.redoStack[history.redoStack.length - 1];
      const { nextState, nextHistory } = redoCommand(domainState, history);

      set({
        domainState: nextState,
        history: nextHistory,
        saveStatus: 'dirty',
      });

      autosave.markDirty(() => ({
        state: get().domainState,
        projectName: get().projectName,
      }));

      if (command && affectsCreativeExecution(command)) get().runExecution();
    },

    canUndo: () => get().history.undoStack.length > 0,
    canRedo: () => get().history.redoStack.length > 0,

    runExecution: async (dirtyNodeIds?: Set<NodeId>) => {
      const runId = ++currentExecutionRunId;
      const { domainState } = get();
      set({ isExecuting: true });

      try {
        const result = await scheduler.executeGraph(domainState, dirtyNodeIds);

        // Discard result if cancelled or superseded by a newer execution request
        if (runId !== currentExecutionRunId || result.errors.aborted) {
          return;
        }

        set({
          isExecuting: false,
          executionResult: result,
          nodeOutputs: {
            ...get().nodeOutputs,
            ...result.outputs,
          },
        });
      } catch (err) {
        if (runId === currentExecutionRunId) {
          set({ isExecuting: false });
        }
        console.error('Execution run failed:', err);
      }
    },

    setProjectName: (name: string) => {
      set({ projectName: name, saveStatus: 'dirty' });
      autosave.markDirty(() => ({
        state: get().domainState,
        projectName: name,
      }));
    },

    loadCanonicalGraph: (canonical: CanonicalGraph, name?: string) => {
      scheduler.reset();
      const nextState = createDomainState(canonical);
      set({
        domainState: nextState,
        history: createEmptyHistory(),
        projectName: name || canonical.name || 'Restored Project',
        nodeOutputs: {},
        saveStatus: 'saved',
      });
      get().runExecution();
    },

    resetToNewProject: (name = 'Untitled Studio Project') => {
      scheduler.reset();
      const nextState = createEmptyGraph(name);
      set({
        domainState: nextState,
        history: createEmptyHistory(),
        projectName: name,
        nodeOutputs: {},
        saveStatus: 'saved',
      });
      get().runExecution();
    },
  };
});
